// Shared named refusal and encounter reload. Delivery retains no undo state.
import { classifyRefusal, type TruthRefusal } from '../shared/revert-on-refusal.js';
import type { MovementTruthTerminalInfo } from '../janus-engine/movement/client-truth-commit.js';
import { ftCurrentTruthSink, trainingGroundsRefusalMessage } from '../shared/truth-sink';

/** The outbox's terminal answer as an R-121 refusal (null = it was not one), with the page warning shown when `lane` is named. */
export function refusalOf(info: MovementTruthTerminalInfo, lane?: string): TruthRefusal | null {
  // the server's own body when the lane passed it (its `reason` is the contract word — Codex A2 #6 P3), else the status + message
  const refusal = classifyRefusal({ status: info.status, body: info.body ?? { error: info.error } });
  if (refusal && lane && typeof displayWarning === 'function') {
    displayWarning(ftCurrentTruthSink() ? trainingGroundsRefusalMessage(info.error, lane) : refusal.reason === 'permission' ? `You do not have permission to act as this creature. Reloading the encounter.`
      : `The ${lane} was refused (${info.error}). Reloading the encounter.`);
  }
  return refusal;
}

const reloadedOperations = new Map<string, number>();
declare global {
  interface Window { ftReloadRefusedEncounter?: (encounterId: string, operationId?: string) => Promise<void> }
}
/** The save answer and socket correction share one recovery, even if the read finished before the other arrives. */
export function reloadRefusedEncounter(encounterId: string, operationId?: string): Promise<void> {
  if (ftCurrentTruthSink()) return Promise.resolve();
  if (window.ftReloadRefusedEncounter && window.ftReloadRefusedEncounter !== reloadRefusedEncounter)
    return window.ftReloadRefusedEncounter(encounterId, operationId);
  encounterId = encounterId.toUpperCase();
  if (!Array.isArray(window.FateTide?.encounterCache?.get(encounterId)?.encounter_creatures_arr)) return Promise.resolve();
  operationId = operationId?.toUpperCase();
  const anonymous = !operationId || operationId === 'CORRECTION:' + encounterId;
  const key = encounterId + ':' + (operationId || 'CORRECTION:' + encounterId);
  const prior = reloadedOperations.get(key);
  if (prior !== undefined && (!anonymous || Date.now() - prior < 5000)) return Promise.resolve();
  reloadedOperations.set(key, Date.now());
  if (reloadedOperations.size > 512) reloadedOperations.delete(reloadedOperations.keys().next().value!);
  window.dispatchEvent(new CustomEvent('ft:truth-correction', { detail: { encounterId, operationId } }));
  return window.ftResyncEncounter?.(encounterId).catch(error => console.warn('Encounter reload failed', error)) ?? Promise.resolve();
}
// Both action and socket bundles can load this adapter; one owner banks recovery ids.
window.ftReloadRefusedEncounter ??= reloadRefusedEncounter;
